import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ISubServiceResponse,
  ISubServiceReq,
  IAddOrUpdateSubService,
  ICommonListPayloadDto,
  IServiceResponse,
} from "../type/type.ts";
import axios from "axios";
import { RootState } from "../store/store.ts";
import Axios from "../axios-config/axiosInstance.ts";
import { ISubService } from "../type/type.ts";

interface SubServiceMasterState {
  subServices: ISubService[];
  loading: boolean;
  error: string | null;
  serviceTypeSelect: {
    label: string;
    value: number;
  }[];
  offset: number;
  limit: number;
  sortField: string;
  sortOrder: number;
  dataCount: number;
  searchInput: string;
}

const initialState: SubServiceMasterState = {
  subServices: [],
  loading: false,
  error: null,
  dataCount: 0,
  offset: 0,
  limit: 10,
  sortField: "createdOn",
  sortOrder: 1,
  serviceTypeSelect: [],
  searchInput: ""
};


export const fetchSubServices = createAsyncThunk<ISubServiceResponse, ICommonListPayloadDto>(
  'subservices/fetch',
  async (payload, { rejectWithValue }) => {
      try {
          const response = await Axios.get<ISubServiceResponse>(`subservice`,{params:payload});
          return response.data;
      } catch (error: any) {
          return rejectWithValue(error.response?.data?.message || "Failed to fetch services");
      }
  }
);

// post new service
export const addSubService = createAsyncThunk<
  IAddOrUpdateSubService,
  ISubServiceReq,
  { rejectValue: string }
>("subservice/add", async (values, { rejectWithValue }) => {
  try {
    const response = await Axios.post<IAddOrUpdateSubService>(
      "subservice",
      values
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to add service"
    );
  }
});

//  update service
export const updateSubService = createAsyncThunk<
  IAddOrUpdateSubService,
  Partial<ISubService> & { id: number; name: string },
  { rejectValue: string }
>("subservice/update", async (value, { rejectWithValue }) => {
  try {
      const response = await Axios.put<IAddOrUpdateSubService>(`subservice/${value.id}`, { name: value.name });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update subservice name");
    }
  }
);

// delete subservice    
export const deleteSubService = createAsyncThunk<
  number, // Return type (deleted ID)
  number, // Input type (ID to delete)
  { rejectValue: string }
>("subservice/delete", async (id, { rejectWithValue }) => {
  try {
    await Axios.delete(`subservice/${id}`);
    return id;
  } catch (error: any) {
    return rejectWithValue(
        error.response?.data?.message || "Failed to delete subservice"
    );
  }
});

export const updateStatus = createAsyncThunk<IAddOrUpdateSubService, { id: number; isActive: boolean }, { rejectValue: string }>(
  "subservice/updateStatus ",
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      const response = await Axios.put<IAddOrUpdateSubService>(`subservice/${id}`, { isActive });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update service status");
    }
  }
);
// Create Slice
const SubServiceMasterSlice = createSlice({
  name: "subserviceMaster",
  initialState,
  reducers: {
    setOffset: (state, action) => {
      state.offset = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSortField: (state, action) => {
      state.sortField = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setSearchInput: (state, action) => {
      state.searchInput = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubServices.fulfilled, (state, action: PayloadAction<ISubServiceResponse>) => {
        state.loading = false;
        state.subServices = action.payload.data.data || [];
        state.dataCount = action.payload.data.total;
      })

      .addCase(fetchSubServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to load subservices";
      })
      .addCase(addSubService.pending, (state) => {
        state.loading = true;
      })
      .addCase(addSubService.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.subServices = [...state.subServices, action.payload.data];
      })

        .addCase(addSubService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to add subservice";
      })

      .addCase(updateSubService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubService.fulfilled, (state, action) => {
        if (!action.payload?.data) {
          console.error("Update payload missing data:", action.payload);
          return;
        }

        const index = state.subServices.findIndex(
            (cat) => cat.id === action.payload.data.id
        );
        if (index !== -1) {
          state.subServices[index] = action.payload.data;
        }

        state.loading = false;
        state.error = null;
      })

      .addCase(updateSubService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update subservice";
      })

      .addCase(deleteSubService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSubService.fulfilled, (state, action) => {
        if (typeof action.payload !== "number") {
          console.error("Delete failed, invalid payload:", action.payload);
          return;
        }

        state.subServices = state.subServices.filter(
          (cat) => cat.id !== action.payload
        );
        state.loading = false;
      })

      .addCase(deleteSubService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete subservice";
      })

      .addCase(updateStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(updateStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })

      .addCase(updateStatus.rejected, (state, action) => {  
        state.loading = false;
        state.error = action.payload || "Failed to update subservice status";
      });
  },
});

export const { setOffset, setLimit, setSortField, setSortOrder, setSearchInput } = SubServiceMasterSlice.actions;
export default SubServiceMasterSlice.reducer;
